import React, { useEffect, useState } from "react";
import { apiProvider } from "../api/core/provider";
import Table from "../components/tables/Table";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
} from "@material-ui/core";
import { SaveRounded, CancelRounded, DeleteRounded } from "@material-ui/icons";
import formatMoney from "../utilities/formatMoney";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function periodLabel(start) {
  if (!start) {return "";}
  const d = new Date(start);
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

const emptyForm = {
  inspectorId: 0,
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  item: "",
  cost: "",
  quantity: 1,
  notes: "",
};

export default function InspectorInvoiceExtraCosts() {
  const [rows, setRows] = useState(null);
  const [inspectors, setInspectors] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [toDelete, setToDelete] = useState(null);

  const fetchInspectors = () => {
    apiProvider.get("inspectors").then((res) => {
      setInspectors(res.data.result || []);
    }).catch((e) => console.log("Error loading inspectors", e));
  };

  const fetchRows = () => {
    apiProvider.get("accounting/inspector-invoice-extra-costs").then((res) => {
      const inspectorMap = {};
      inspectors.forEach((i) => { inspectorMap[i.id] = i.name; });
      const data = res.data.result || [];
      setRows(data.map((ec) => ({
        Id: ec.Id,
        Inspector: inspectorMap[ec.InspectorId] || `#${ec.InspectorId}`,
        Period: periodLabel(ec.Start),
        Item: ec.Item,
        Quantity: ec.Quantity,
        UnitCost: formatMoney(ec.Cost / 100),
        Total: formatMoney(ec.Cost * ec.Quantity / 100),
        Notes: ec.Notes || "",
        raw: ec,
        edit: <button className="btn btn-primary btn-sm" onClick={() => openEdit(ec)}>Edit</button>,
        delete: <button className="btn btn-danger btn-sm" onClick={() => setToDelete(ec)}>Delete</button>,
      })));
    }).catch((e) => console.log("Error loading extra costs", e));
  };

  useEffect(() => { fetchInspectors(); }, []);
  useEffect(() => { fetchRows(); }, [inspectors]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (ec) => {
    setEditing(ec);
    setForm({
      inspectorId: ec.InspectorId,
      year: new Date(ec.Start).getUTCFullYear(),
      month: new Date(ec.Start).getUTCMonth() + 1,
      item: ec.Item,
      cost: ec.Cost / 100,
      quantity: ec.Quantity,
      notes: ec.Notes || "",
    });
    setModalOpen(true);
  };

  const save = () => {
    const payload = {
      inspectorId: Number(form.inspectorId),
      year: Number(form.year),
      month: Number(form.month),
      item: form.item,
      cost: Math.round(Number(form.cost) * 100),
      quantity: Number(form.quantity) || 1,
      notes: form.notes,
    };
    const done = () => { fetchRows(); setModalOpen(false); };
    if (editing) {
      apiProvider.put(`accounting/inspector-invoice-extra-costs/${editing.Id}`, payload).then(done).catch((e) => console.log("Error updating extra cost", e));
    } else {
      apiProvider.post("accounting/inspector-invoice-extra-costs", payload).then(done).catch((e) => console.log("Error creating extra cost", e));
    }
  };

  const confirmDelete = () => {
    apiProvider.delete(`accounting/inspector-invoice-extra-costs/${toDelete.Id}`).then(() => {
      fetchRows();
      setToDelete(null);
    }).catch((e) => console.log("Error deleting extra cost", e));
  };

  const cols = [
    { Header: "Inspector", accessor: "Inspector" },
    { Header: "Period", accessor: "Period" },
    { Header: "Item", accessor: "Item" },
    { Header: "Qty", accessor: "Quantity" },
    { Header: "Unit Cost", accessor: "UnitCost" },
    { Header: "Total", accessor: "Total" },
    { Header: "Notes", accessor: "Notes" },
    { Header: "", accessor: "edit" },
    { Header: "", accessor: "delete" },
  ];

  const yearOptions = [];
  const thisYear = new Date().getFullYear();
  for (let y = thisYear - 1; y <= thisYear + 1; y++) {yearOptions.push(y);}

  return (
    <>
      <h1>Inspector Invoice Extra Costs</h1>
      <div className="tw-mb-4 tw-text-sm tw-text-gray-600">
        Manual extra-cost line items added to an inspector&apos;s invoice for a specific month.
        The invoice shows item, quantity, unit cost and total; notes are admin-only and do not appear on the invoice.
      </div>

      <button className="btn btn-primary tw-mb-6" onClick={openCreate}>Add Extra Cost</button>

      {rows &&
        <div className="tw-mb-10">
          <Table data={rows} columns={cols} />
        </div>
      }

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <DialogContentText>
            {editing ? "Edit extra cost" : "Add an extra cost to an inspector invoice"}
          </DialogContentText>

          <div className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-4">
            <label className="tw-block">
              <span className="tw-text-sm tw-font-medium">Inspector</span>
              <select className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-p-2" value={form.inspectorId}
                onChange={(e) => setForm({ ...form, inspectorId: Number(e.target.value) })}>
                <option value={0}>Choose inspector…</option>
                {inspectors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </label>

            <label className="tw-block">
              <span className="tw-text-sm tw-font-medium">Month</span>
              <select className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-p-2" value={form.month}
                onChange={(e) => setForm({ ...form, month: Number(e.target.value) })}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </label>

            <label className="tw-block">
              <span className="tw-text-sm tw-font-medium">Year</span>
              <select className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-p-2" value={form.year}
                onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}>
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>

            <label className="tw-block tw-col-span-2">
              <span className="tw-text-sm tw-font-medium">Item</span>
              <input className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-p-2" value={form.item}
                onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="e.g. Travel allowance" />
            </label>

            <label className="tw-block">
              <span className="tw-text-sm tw-font-medium">Unit cost ($)</span>
              <input type="number" className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-p-2" value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })} />
            </label>

            <label className="tw-block">
              <span className="tw-text-sm tw-font-medium">Quantity</span>
              <input type="number" min="1" className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-p-2" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
            </label>

            <label className="tw-block tw-col-span-2">
              <span className="tw-text-sm tw-font-medium">Notes (not shown on invoice)</span>
              <textarea className="tw-block tw-w-full tw-rounded-md tw-border-gray-300 tw-p-2" rows={3} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </label>
          </div>
        </DialogContent>
        <DialogActions className="sm:tw-m-4">
          <Button variant="contained" onClick={save} color="primary" startIcon={<SaveRounded />}>Save</Button>
          <Button variant="contained" onClick={() => setModalOpen(false)} color="secondary" startIcon={<CancelRounded />}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
        <DialogContent>
          <DialogContentText>
            Delete this extra cost{toDelete ? ` (${toDelete.Item})` : ""}? This removes it from the inspector&apos;s invoice total.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={confirmDelete} color="secondary" startIcon={<DeleteRounded />}>Delete</Button>
          <Button variant="contained" onClick={() => setToDelete(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}